import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { type CalendarSource } from "./calendarUiSlice";

export type { CalendarSource };

export interface CalendarEventDTO {
  id: string;
  startISO: string;
  endISO: string;
  title: string;
  bgColor: string;
  color: string;
  appType: CalendarSource | string;
  // Optional IDs for navigation
  bookingID?: number | string;
  customEventID?: number | string;
}

export interface DateRangeDTO {
  startISO: string;
  endISO: string;
}

export interface CalendarEventsState {
  // merged view, if needed by consumers
  events: CalendarEventDTO[];
  bySource: Record<CalendarSource, Record<string, CalendarEventDTO>>;
  loadedRanges: Record<CalendarSource, DateRangeDTO[]>;
}

const initialState: CalendarEventsState = {
  events: [],
  bySource: {
    bookings: {},
    custom: {},
    holidays: {},
    service: {},
    serviceEvents: {},
  },
  loadedRanges: {
    bookings: [],
    custom: [],
    holidays: [],
    service: [],
    serviceEvents: [],
  },
};

const calendarEventsSlice = createSlice({
  name: "calendarEvents",
  initialState,
  reducers: {
    addEvents(
      state,
      action: PayloadAction<{
        source: CalendarSource;
        events: CalendarEventDTO[];
      }>
    ) {
      const { source, events } = action.payload;
      const srcMap = { ...state.bySource[source] };
      for (const ev of events) {
        srcMap[ev.id] = ev;
      }
      state.bySource[source] = srcMap;
      // rebuild merged events
      const merged: CalendarEventDTO[] = [];
      for (const s of Object.keys(state.bySource) as CalendarSource[]) {
        merged.push(...Object.values(state.bySource[s]));
      }
      state.events = merged;
      // no-op
    },
    clearRange(
      state,
      action: PayloadAction<{
        source: CalendarSource;
        range: DateRangeDTO;
      }>
    ) {
      const { source, range } = action.payload;
      const srcMap = state.bySource[source] || {};
      const startMs = new Date(range.startISO).getTime();
      const endMs = new Date(range.endISO).getTime();
      const nextMap: Record<string, CalendarEventDTO> = {};
      for (const [id, ev] of Object.entries(srcMap)) {
        const evStart = new Date(ev.startISO).getTime();
        const evEnd = new Date(ev.endISO).getTime();
        if (Number.isNaN(evStart) || Number.isNaN(evEnd)) {
          nextMap[id] = ev;
          continue;
        }
        // Remove events that overlap the target range: [evStart, evEnd) ∩ [start, end) ≠ ∅
        const overlaps = evStart < endMs && evEnd > startMs;
        if (!overlaps) nextMap[id] = ev;
      }
      state.bySource[source] = nextMap;
      // rebuild merged events
      const merged: CalendarEventDTO[] = [];
      for (const s of Object.keys(state.bySource) as CalendarSource[]) {
        merged.push(...Object.values(state.bySource[s]));
      }
      state.events = merged;
      // no-op
    },
    markRange(
      state,
      action: PayloadAction<{
        source: CalendarSource;
        range: DateRangeDTO;
      }>
    ) {
      const { source, range } = action.payload;
      state.loadedRanges[source] = [...state.loadedRanges[source], range];
    },
    clearSource(state, action: PayloadAction<CalendarSource>) {
      const src = action.payload;
      state.bySource[src] = {};
      state.loadedRanges[src] = [];
      // rebuild merged events
      const merged: CalendarEventDTO[] = [];
      for (const s of Object.keys(state.bySource) as CalendarSource[]) {
        merged.push(...Object.values(state.bySource[s]));
      }
      state.events = merged;
    },
  },
});

export const { addEvents, markRange, clearSource, clearRange } =
  calendarEventsSlice.actions;
export default calendarEventsSlice.reducer;
